import type {
	PrefetchVariant,
	Property,
	ShortVideoProperty,
	VideoViewInfo
} from '~/core/entities/property';

export type VideoType = 'native' | 'hls';

/** Entry-point property for Shorts analytics (`inicial_*` Mixpanel props). */
export interface ShortsEntryContext {
	entryCommercialId: string;
	entryAskingPrice: number | null;
	entryRegionName: string;
	/**
	 * True quando `entryCommercialId` veio de uma URL direta (sem carrossel de
	 * origem). O item desse commercialId usa poster `lqip` para nao disputar
	 * banda com o primeiro chunk do video.
	 */
	isDirectEntry?: boolean;
}

export type ReelItemProperty = Property | ShortVideoProperty;

interface FeedMediaItemBase<
	TProperty extends ReelItemProperty = ReelItemProperty
> {
	property: TProperty;
	reelKey?: string;
	thumbnailUrl: string | null;
	images?: string[];
	videoType?: VideoType;
	videoView?: VideoViewInfo;
	isFromHistory?: boolean;
	prefetchVariants?: PrefetchVariant[];
	/** Video width in pixels - used to determine portrait/landscape orientation */
	videoWidth?: number;
	/** Video height in pixels - used to determine portrait/landscape orientation */
	videoHeight?: number;
}

export interface FeedVideoItem<
	TProperty extends ReelItemProperty = ReelItemProperty
> extends FeedMediaItemBase<TProperty> {
	kind: 'video';
	videoUrl: string;
}

export interface FeedPhotosItem<
	TProperty extends ReelItemProperty = ReelItemProperty
> extends FeedMediaItemBase<TProperty> {
	kind: 'fotos';
	videoUrl: null;
	images: string[];
}

/**
 * Item visual montado pelo consumidor dentro do mesmo carrossel vertical.
 * O feed só conhece o slot a renderizar; a regra de negócio fica na página.
 */
export interface FeedCustomItem {
	kind: 'custom';
	reelKey: string;
	slot: string;
}

export type FeedMediaItem<
	TProperty extends ReelItemProperty = ReelItemProperty
> = FeedVideoItem<TProperty> | FeedPhotosItem<TProperty>;
export type FeedItem = FeedMediaItem | FeedCustomItem;

/** Nomes legados mantidos enquanto os consumidores migram para FeedItem. */
export type ReelItem = FeedMediaItem;
export type ReelCustomItem = FeedCustomItem;
export type ReelFeedItem = FeedItem;

export function isReelCustomItem(
	item: FeedItem | null | undefined
): item is FeedCustomItem {
	return item?.kind === 'custom';
}

export function isFeedVideoItem(
	item: FeedItem | null | undefined
): item is FeedVideoItem {
	return item?.kind === 'video';
}

export function isFeedPhotosItem(
	item: FeedItem | null | undefined
): item is FeedPhotosItem {
	return item?.kind === 'fotos';
}

export function getReelFeedItemKey(item: FeedItem): string {
	if (item.reelKey) return item.reelKey;
	if (isReelCustomItem(item)) return `custom:${item.slot}`;
	return item.property.commercialId;
}

export function findReelFeedItemIndex(
	items: FeedItem[],
	target: FeedItem
): number {
	const targetKey = getReelFeedItemKey(target);
	const exactIndex = items.findIndex(
		item => getReelFeedItemKey(item) === targetKey
	);
	if (exactIndex >= 0 || isReelCustomItem(target)) return exactIndex;

	return items.findIndex(
		item =>
			!isReelCustomItem(item) &&
			item.property.commercialId === target.property.commercialId
	);
}

export interface PropertyReelsFeedProps {
	items: FeedItem[];
	initialIndex?: number;
	initialMuted?: boolean;
	preloadFirst?: boolean;
	videoType?: VideoType;
	isLoading?: boolean;
	isReady?: boolean;
	hasMore?: boolean;
	hasMoreHistory?: boolean;
	/** Number of history items prepended before the entry-point item */
	historyCount?: number;
	showNavigation?: boolean;
	showExploreButton?: boolean;
	isPaused?: boolean;
	disableScroll?: boolean;
	emptyStateTitle?: string;
	emptyStateDescription?: string;
	emptyStateActionLabel?: string;
	hideHeaderControls?: boolean;
	disableKeyboard?: boolean;
	/** Enables Shorts Mixpanel payloads (`shorts_viewed`, share extras, etc.). */
	shortsEntry?: ShortsEntryContext;
	autoplayArmed?: boolean;
}

export interface PropertyReelsFeedItemProps {
	item: ReelItem;
	isActive: boolean;
	isCurrent?: boolean;
	index: number;
	shouldLoad: boolean;
	videoType?: VideoType;
	isGlobalMuted?: boolean;
	isMobile?: boolean;
	/** When true, uses lighter buffer settings to reduce bandwidth while preloading */
	isPreloadOnly?: boolean;
}

export type PropertyReelsCloseReason = 'backdrop' | 'escape' | 'ui';

export interface PropertyReelsFeedEmits {
	(e: 'close', reason?: PropertyReelsCloseReason): void;
	(e: 'property-clicked', property: ReelItemProperty): void;
	/** Índice em `items`: todo slide é um item da lista, sem compensação. */
	(e: 'index-change', index: number): void;
	(e: 'current-item-change', item: ReelItem | null, itemIndex: number): void;
	(e: 'first-video-loaded'): void;
	(e: 'open-explore'): void;
	(e: 'empty-state-action'): void;
	(e: 'request-history'): void;
	(e: 'tap'): void;
}

export interface PropertyReelsFeedItemEmits {
	(e: 'property-clicked', property: ReelItemProperty): void;
	(e: 'video-play'): void;
	(e: 'video-pause'): void;
	(e: 'video-loaded'): void;
	(e: 'video-buffered'): void;
	/** Fires when the video first starts playing (earlier than buffered) */
	(e: 'video-playing'): void;
	(e: 'tap'): void;
	/** Fires when the active carousel image changes (image-only reels, desktop bg). */
	(e: 'carousel-image-change', url: string | null): void;
}
